import axios, { AxiosError } from 'axios';
import { isNil, mapValues } from 'lodash';
import { parse } from 'date-fns';
import { ColumnId } from '../components/InvestmentTable/columns';
import { TimeRange, IntervalStatsMap } from '../types';
import { backendUrl } from '../constants';
import { getPortfolios, updatePortfolio } from '../storage';
import { Sentry } from '../utils/sentryConfig';
import { InvestorType } from '../utils/smartChatPrompts';
import { getRevenueCatAppUserId } from '../utils/revenueCatUtils';
import i18n from '../i18n';

/** Returns non-English language code for API translation, or undefined for English. */
const getApiLanguage = (): string | undefined => {
  const lang = i18n.language?.split('-')[0];
  return lang && lang !== 'en' ? lang : undefined;
};

const request = axios.create({
  baseURL: backendUrl,
  timeout: 30000, // 30 second timeout - prevents "timeout of 0ms" errors on slow networks
});

// Add request interceptor for debugging
request.interceptors.request.use(
  (config) => {
    console.log(`[AXIOS] Request to ${config.url}:`, {
      method: config.method,
      headers: config.headers,
      url: config.url,
    });
    return config;
  },
  (error) => {
    console.error('[AXIOS] Request error:', error);
    return Promise.reject(error);
  },
);

// Add response interceptor for debugging and network error detection
request.interceptors.response.use(
  (response) => {
    console.log(`[AXIOS] Response from ${response.config.url}:`, {
      status: response.status,
      data: response.data,
    });

    // Add successful API call breadcrumb for Sentry
    Sentry.addBreadcrumb({
      category: 'http',
      type: 'http',
      data: {
        method: response.config.method?.toUpperCase(),
        url: response.config.url,
        status: response.status,
      },
      level: 'info',
    });

    return response;
  },
  (error) => {
    // Check if it's an axios error (compatible with axios 0.19.0)
    if (error && error.isAxiosError) {
      const status = error.response?.status;
      const url = error.config?.url;
      const method = error.config?.method?.toUpperCase();

      if (!error.response) {
        // Network error - no response received from server
        console.error('[AXIOS] Network error - no connection to server:', {
          url: error.config?.url,
          message: error.message,
        });
      } else if (error.code === 'ECONNABORTED') {
        // Request timeout
        console.error('[AXIOS] Request timeout:', {
          url: error.config?.url,
          timeout: error.config?.timeout,
        });
      } else {
        // HTTP error with response
        console.error('[AXIOS] HTTP error:', {
          url: error.config?.url,
          status: error.response.status,
          data: error.response.data,
        });
      }

      // Add failed request breadcrumb
      Sentry.addBreadcrumb({
        category: 'http',
        type: 'error',
        data: {
          method,
          url,
          status,
          statusText: error.response?.statusText,
        },
        level: 'error',
      });

      // Capture exception with detailed context
      // Skip 401s (handled by auth), 404s on charts endpoint (expected when data unavailable),
      // aborted requests (user navigated away), and timeout/network errors (transient connectivity)
      const isChartsEndpoint = url?.includes('/charts/');
      const isExpected404 = status === 404 && isChartsEndpoint;
      const isAbortedRequest = error.code === 'ERR_CANCELED' || error.message?.toLowerCase().includes('aborted');
      const isTimeoutError = error.code === 'ECONNABORTED' || error.message?.toLowerCase().includes('timeout');
      const isNetworkError = !error.response && !isAbortedRequest && !isTimeoutError;
      if (status !== 401 && !isExpected404 && !isAbortedRequest && !isTimeoutError && !isNetworkError) {
        Sentry.captureException(error, {
          tags: {
            http_status: status?.toString() || 'network_error',
            http_method: method || 'unknown',
            api_endpoint: url || 'unknown',
          },
          extra: {
            request: {
              url,
              method,
              headers: error.config?.headers,
              params: error.config?.params,
            },
            response: {
              status,
              statusText: error.response?.statusText,
              data: error.response?.data,
            },
          },
        });
        // Mark error as reported to prevent double-capture
        (error as any).__sentryReported = true;
      }
    } else {
      console.error('[AXIOS] Response error:', error);
      // Only capture if not already reported by interceptor
      if (!(error as any).__sentryReported) {
        Sentry.captureException(error);
      }
    }
    return Promise.reject(error);
  },
);

export interface BasicInvestment {
  latestPrice: number;
  changePercent1d: number;
  changePercent1w: number;
  changePercent1m: number;
  changePercent3m: number;
  changePercent6m: number;
  changePercentYtd: number;
  changePercent1y: number;
  changePercent5y: number;
  symbol: string;
  name: string;
  marketCap: number;
  category: string;
  sector: string;
  industry: string;
  week52High: number;
  week52Low: number;
  description: string;
  currency?: string;
  website?: string;
  ceo?: string;
  employees?: number;
  headquarters?: string;
  priceToEarnings?: number;
  priceToSales?: number;
  priceToSalesGrowth?: number;
  revenueGrowth1y?: number;
  percentMarketCapTraded1d?: number;
  cashflowYield?: number;
  capexToRevenue?: number;
  evToGrossProfit?: number;
  insidersPercentTraded3m?: number;
  bottomLine?: {
    growthRating: Rating;
    insiderRating: Rating;
    priceRating: Rating;
    profitRating: Rating;
    returnRating: Rating | null;
    riskRating: Rating | null;
    sizeRating: Rating | null;
    feesRating: Rating | null;
  };
  _isPlaceholder?: boolean;
}

export type GetInvestmentsResponse = BasicInvestment[];
export const getBasicInvestments = async (symbols: string[]): Promise<GetInvestmentsResponse> =>
  (await request.get(`/investments/?symbols=${symbols.join(',')}`)).data.results;

export const getBasicInvestment = async (symbol: string): Promise<BasicInvestment> =>
  (await request.get(`/investments/${symbol}/`, { params: { language: getApiLanguage() } })).data.results;

const generateDate = (result: any) => {
  if (!result.date) return result;

  const date = result.minute
    ? parse(`${result.date} ${result.minute}`, 'yyyy-MM-dd HH:mm', new Date())
    : parse(result.date, 'yyyy-MM-dd', new Date());
  return { ...result, date };
};

export const getCharts = async (symbol: string): Promise<Record<TimeRange, ChartElement[]>> => {
  try {
    const response = await request.get(`/investments/${symbol}/charts/`, {
      params: {
        wiims: true,
        language: getApiLanguage(),
      },
    });
    return mapValues(response.data.results, (chartElements) =>
      chartElements.map(generateDate).filter((result: any) => !isNil(result.price) && !isNil(result.date)),
    ) as {
      [index in TimeRange]: ChartElement[];
    };
  } catch (error: any) {
    // Handle 404 gracefully - chart data may not be available for all symbols
    // This is expected behavior, not an error condition worth reporting
    if (error?.response?.status === 404) {
      return {} as Record<TimeRange, ChartElement[]>;
    }
    throw error;
  }
};

export const getStockPeers = async (symbol: string): Promise<BasicInvestment[]> => {
  try {
    return (await request.get(`/investments/${symbol}/peers/`)).data.results;
  } catch (error: any) {
    // Handle 404 gracefully - no peers is expected for many symbols
    // Fixes BLOOM-FRONTEND-WEB-VJ and BLOOM-FRONTEND-WEB-WD
    if (error?.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

export interface TechnicalIndicators {
  sma_200: Record<string, number>;
  sma_50: Record<string, number>;
  bband_upper: Record<string, number>;
  bband_lower: Record<string, number>;
  [key: string]: any;
}

export interface TechnicalsResponse {
  symbol: string;
  signal: {
    rating: string;
    confidence: number;
    score: number;
  };
  chart_data: Record<string, any>;
  technical_indicators: TechnicalIndicators;
  rsi_regions: { start_date: string; end_date: string; type: string }[];
  data_interval?: '15min' | '60min' | 'daily' | 'weekly';
}

export const getTechnicals = async (symbol: string): Promise<TechnicalsResponse> => {
  const response = await request.get(`/investments/${symbol}/technicals/`);
  const data = response.data.results;
  if (data.chart_data) {
    data.chart_data = mapValues(data.chart_data, (chartElements: any[]) =>
      chartElements.map(generateDate).filter((result: any) => !isNil(result.price) && !isNil(result.date)),
    );
  }
  return data as TechnicalsResponse;
};

export const getTechnicalsForInterval = async (symbol: string, interval: string): Promise<TechnicalsResponse> => {
  const response = await request.get(`/investments/${symbol}/technicals/${interval}/`);
  const data = response.data.results;
  if (data.chart_data) {
    data.chart_data = mapValues(data.chart_data, (chartElements: any[]) =>
      chartElements.map(generateDate).filter((result: any) => !isNil(result.price) && !isNil(result.date)),
    );
  }
  return data as TechnicalsResponse;
};

export interface Stats {
  revenue: number;
  grossMargin: number;
  netMargin: number;
  operatingCashflowMargin1y: number;
  freeCashflowMargin: number;
  earnings: number;
  revenueGrowth: number;
  earningsGrowth: number;
  threeYearRevenueGrowth: number;
  threeYearEarningsGrowth: number;
  rdSpend: number;
  priceToEarnings: number;
  priceToEarningsGrowth: number;
  priceToBook: number;
  priceToSales: number;
  priceToSalesGrowth: number;
  enterpriseValue: number;
  evToSales: number;
  evToSalesGrowth: number;
  evToEbitda: number;
  evToGrossProfit: number;
  evToCashflow: number;
  evToCashflowGrowth: number;
  ebitda1y: number;
  cashflowYield: number;
  capexToRevenue: number;
  capitalExpenditures1y: number;
  earningsYield: number;
  returnOnCapital: number;
  returnOnEquity: number;
  returnOnAssets: number;
  salesOnCapital: number;
  cashflowReturnOnCapital: number;
  assets: number;
  cash: number;
  debt: number;
  cashToDebt: number;
  equityToDebt: number;
  marketCap: number;
  ebitda: number;
  freeCashFlow: number;
  dividendYield: number;
  rdMargin1y: number;
  salesMargin1y: number;
  shareGrowth1q: number;
  shareGrowthQq: number;
  dollarsBought: number;
  dollarsSold: number;
  dollarsExercised: number;
  dollarsGranted: number;
  dollarsOther: number;
  netDollarsTraded: number;
  netPercentTraded: number;
  totalCapital: number;
  beta: number;
  shortPercent: number;
  latestReportDate: string;
  currency?: string;
}
export const getStockStats = async (symbol: string): Promise<Stats> =>
  (await request.get(`/investments/${symbol}/stats/`)).data.results;

export interface FinancialStatement {
  fiscalDate: string;
  isAnnual: boolean;
  reportedCurrency: string;
  // Income Statement
  totalRevenue: number | null;
  costOfRevenue: number | null;
  grossProfit: number | null;
  operatingIncome: number | null;
  operatingExpenses: number | null;
  ebit: number | null;
  ebitda: number | null;
  netIncome: number | null;
  researchAndDevelopment: number | null;
  sellingGeneralAndAdministrative: number | null;
  interestExpense: number | null;
  incomeTaxExpense: number | null;
  // Balance Sheet
  totalAssets: number | null;
  totalCurrentAssets: number | null;
  cash: number | null;
  cashAndShortTermInvestments: number | null;
  netReceivables: number | null;
  inventory: number | null;
  propertyPlantEquipment: number | null;
  goodwill: number | null;
  intangibleAssets: number | null;
  totalLiabilities: number | null;
  totalCurrentLiabilities: number | null;
  accountsPayable: number | null;
  deferredRevenue: number | null;
  longTermDebt: number | null;
  shortTermDebt: number | null;
  totalShareholderEquity: number | null;
  retainedEarnings: number | null;
  commonStock: number | null;
  // Cash Flow
  operatingCashflow: number | null;
  capitalExpenditures: number | null;
  cashflowFromFinancing: number | null;
  cashflowFromInvestment: number | null;
  depreciationAndAmortization: number | null;
  changeInCash: number | null;
}

export interface FinancialsResponse {
  quarterly: FinancialStatement[];
  annual: FinancialStatement[];
}

export const getFinancials = async (symbol: string): Promise<FinancialsResponse> =>
  (await request.get(`/investments/${symbol}/financials/`)).data.results;

export interface Holding {
  symbol: string; // Backend filters out holdings without symbols
  name: string;
  weight: number;
}
export const getHoldings = async (symbol: string): Promise<Holding[]> =>
  (await request.get(`/investments/${symbol}/holdings/`)).data.results;

export interface GetOverviewResponse {
  attributes: string[];
  items: { title: string; text: string; rating: number }[];
}
export const getOverview = async (symbol: string): Promise<GetOverviewResponse> =>
  (await request.get(`/investments/${symbol}/overview/`)).data.results;

export enum Rating {
  Exceptional = 'EXCEPTIONAL',
  Favorable = 'FAVORABLE',
  Average = 'AVERAGE',
  Unfavorable = 'UNFAVORABLE',
  Dangerous = 'DANGEROUS',
  Missing = 'MISSING',
}

export interface GetBottomLineResponse {
  details: {
    rating: Rating;
    title: string;
    descriptions: string[];
  }[];
}

export const getBottomLine = async (symbol: string): Promise<GetBottomLineResponse> =>
  (await request.get(`/investments/${symbol}/bottom_line_v2/`, { params: { language: getApiLanguage() } })).data
    .results;

export interface NewsArticle {
  articleSource: string;
  articleUrl: string;
  headline: string;
  imageUrl: string;
  publishDateDisplayText: string;
  hasPaywall: boolean;
}
export const getNews = async (symbol: string): Promise<NewsArticle[]> =>
  (await request.get(`/investments/${symbol}/news/`)).data.results;

export type CollectionInvestment = BasicInvestment & {
  bottomLine?: { [key: string]: Rating };
  rationale?: string;
  allocation_percentage?: number;
};
export interface Collection {
  id: number;
  active: boolean;
  restrictedToSubscribers: boolean;
  name: string;
  slug: string;
  description: string;
  whyItMatters?: string;
  updated: string;
  imageId?: string;
  investments: CollectionInvestment[];
  defaultMetric: ColumnId;
  defaultSortMetric?: ColumnId;
  defaultSortDesc?: boolean;
  sector: string;
  section: string;
  investmentStyle?: string;
  timeHorizon?: string;
}

export const getCollection = async (id: string): Promise<Collection> => (await request.get(`/collections/${id}/`)).data;

export const getCollectionsList = async (): Promise<Omit<Collection, 'investments'>[]> =>
  (
    await request.get(`/collections/`, {
      params: {
        version: 2,
        // show_all: true,
      },
    })
  ).data.results;

export const getCollectionReturns = async (
  collectionIds: number[],
  timerange: string,
): Promise<Record<number, number | null>> =>
  (
    await request.get(`/collections/returns/`, {
      params: {
        collection_ids: collectionIds.join(','),
        timerange,
      },
    })
  ).data;

export const getRelatedCollections = async (symbol: string): Promise<Omit<Collection, 'investments'>[]> =>
  (
    await request.get(`/investments/${symbol}/collections/`, {
      params: {
        version: 2,
      },
    })
  ).data.results;

export interface CollectionAllocation {
  id: number;
  collection: number;
  date: string;
  title: string;
  strategy: string;
  allocations: Array<{
    symbol: string;
    allocation: number; // Note: Backend returns 'allocation', but storage uses 'allocation_percentage'
    rationale: string;
    name?: string;
  }>;
  totalAllocation: string;
  createdAt: string;
}

export const getCollectionAllocation = async (id: string, date?: string): Promise<CollectionAllocation | null> => {
  const params = date ? { date } : {};
  const response = await request.get(`/collections/${id}/allocation/`, { params });
  return response.data || null;
};

export interface Wiim {
  title: string;
  created: string;
  updated: string;
}

export interface WiimsPagination {
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface WiimsResponse {
  results: {
    [symbol: string]: Wiim[];
  };
  pagination: WiimsPagination;
}

export const getWiims = async (
  symbols: string[],
  { dateFrom, page = 0, pageSize = 20 }: { dateFrom?: string; page?: number; pageSize?: number } = {},
): Promise<WiimsResponse> => {
  const params: Record<string, string> = {
    symbols: symbols.join(','),
    page: page.toString(),
    pageSize: pageSize.toString(),
  };
  if (dateFrom) params.dateFrom = dateFrom;
  const lang = getApiLanguage();
  if (lang) params.language = lang;
  const querystring = new URLSearchParams(params).toString();
  return (await request.get(`/investments/wiims/?${querystring}`)).data;
};

interface DeviceRegistration {
  notificationTypes?: string[];
  watchlistSymbols?: string[];
  revenuecatUserId?: string | null;
  language?: string;
}

export const registerDevice = async (token: string, platform: string, config: DeviceRegistration) => {
  const body: Record<string, unknown> = { token, platform };
  if (config.notificationTypes !== undefined) {
    body.notificationTypes = config.notificationTypes;
  }
  if (config.watchlistSymbols !== undefined) {
    body.watchlistSymbols = config.watchlistSymbols;
  }
  // Only include revenuecatUserId when the caller explicitly provides it, so that
  // language-only calls (handleLanguageChange) don't clear a previously stored value.
  // When provided, send null so the backend can clear a stale id on sign-out.
  if ('revenuecatUserId' in config) {
    body.revenuecatUserId = config.revenuecatUserId ?? null;
  }
  if (config.language) {
    body.language = config.language;
  }
  return (await request.post('/api/register-device/', body)).data;
};

export interface RiskFactor {
  id: string;
  keyPoint: string;
  details?: string;
  sourceDoc: string;
}
export const getRiskFactors = async (symbol: string): Promise<RiskFactor[]> => {
  return (await request.get(`/investments/${symbol}/risk_factors`)).data.results;
};

export const sendFeedback = async (data: { text: string; email?: string }) => {
  return (await request.post(`/feedback/`, data)).data.results;
};

export interface Insight {
  id: number;
  keyPoints: {
    page: number;
    details: string;
  }[];
  potentialRisks: {
    page: number;
    details: string;
  }[];
  fileUrl: string;
  created: string;
  updated: string;
  summary: string;
  title: string;
  articleDate: string;
  investment: string;
}

export const getInsights = async (symbol: string): Promise<Insight[]> => {
  return (await request.get(`/investments/${symbol}/insights/`, { params: { language: getApiLanguage() } })).data
    .results;
};

// Content block for multimodal messages (text + images)
export interface ContentBlock {
  type: 'text' | 'image_url';
  text?: string;
  image_url?: {
    url: string; // base64 data URL or HTTPS URL
    detail?: 'low' | 'high' | 'auto';
  };
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

// New interface for API messages with multimodal support
export interface ApiMessage {
  role: 'user' | 'assistant';
  content: string | ContentBlock[]; // Support both text-only and multimodal
}

export interface ChatResponse {
  text: string;
  citations: string[];
}

export const sendChatMessage = async (history: ApiMessage[]): Promise<ChatResponse> => {
  return (
    await request.post('/chat-agent/', {
      messages: history,
    })
  ).data;
};

// Streaming chat function
export const streamChatMessage = async (userQuery: string, onStreamEvent: (event: any) => void): Promise<void> => {
  try {
    const response = await fetch(`${backendUrl}/chat-agent/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages: [
          {
            role: 'user',
            content: userQuery,
          },
        ],
        stream: true,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    await handleStreamingResponse(response, onStreamEvent);
  } catch (error) {
    console.error('Streaming error:', error);

    // Provide user-friendly error messages based on error type
    let userMessage = 'An unexpected error occurred. Please try again.';

    if (error instanceof TypeError && error.message.includes('fetch')) {
      userMessage = 'Network connection failed. Please check your internet connection and try again.';
    } else if (error instanceof Error && error.message.includes('HTTP error')) {
      userMessage = 'Server error. Please try again in a moment.';
    } else if (error instanceof Error && error.message.includes('not readable')) {
      userMessage = 'Unable to process the response. Please try again.';
    } else if (error instanceof Error) {
      // Log the actual error but show a user-friendly message
      userMessage = 'Something went wrong while processing your request. Please try again.';
    }

    onStreamEvent({ type: 'error', message: userMessage });
  }
};

// Custom error class for HTTP errors with status code and optional Retry-After
class HttpError extends Error {
  constructor(
    public statusCode: number,
    public retryAfter?: number,
  ) {
    super(`HTTP error! status: ${statusCode}`);
    this.name = 'HttpError';
  }
}

// Portfolio context for chat agent
export interface PortfolioContextAllocation {
  symbol: string;
  allocation_percentage?: number;
}

export interface PortfolioContextItem {
  title: string;
  symbols: string[];
  allocations?: PortfolioContextAllocation[];
}

export interface PortfolioContext {
  portfolios: PortfolioContextItem[];
}

// Re-export InvestorType as InvestorPersona for API compatibility
export type InvestorPersona = InvestorType;

// Sprint 3d: SSE buffering verified — /chat-agent/ sets X-Accel-Buffering: no and
// Cache-Control: no-cache in chat_agent_simple.py. The 'connected' event fires before
// agent/LLM setup. Backend tests (test_chat_agent_simple.py) assert both headers.
export const streamChatMessageWithHistory = async (
  messages: ApiMessage[],
  onStreamEvent: (event: any) => void,
  signal?: AbortSignal,
  portfolioContext?: PortfolioContext,
  investorPersona?: InvestorPersona,
  model?: string,
  language?: string,
): Promise<void> => {
  const MAX_RETRIES = 2;
  const BASE_DELAY_MS = 1000;
  const RETRYABLE_STATUS_CODES = [429, 502, 503, 504];

  // Get RevenueCat user ID for subscription verification (only needed for premium models)
  const revenueCatUserId = model ? await getRevenueCatAppUserId() : undefined;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    // Check if request was aborted before each attempt
    if (signal?.aborted) {
      onStreamEvent({ type: 'aborted' });
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/chat-agent/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(revenueCatUserId && { 'X-RevenueCat-User-ID': revenueCatUserId }),
        },
        body: JSON.stringify({
          messages: messages,
          stream: true,
          ...(portfolioContext && { portfolio_context: portfolioContext }),
          ...(investorPersona && { investor_persona: investorPersona }),
          ...(model && { model }),
          ...(language && language.split('-')[0] !== 'en' && { language }),
        }),
        signal,
      });

      // Check for retryable HTTP status codes before throwing
      if (!response.ok) {
        // Parse Retry-After header for rate limiting (429)
        let retryAfterMs: number | undefined;
        const retryAfterHeader = response.headers.get('Retry-After');
        if (retryAfterHeader) {
          const retryAfterSeconds = parseInt(retryAfterHeader, 10);
          if (!isNaN(retryAfterSeconds)) {
            retryAfterMs = retryAfterSeconds * 1000;
          }
        }
        throw new HttpError(response.status, retryAfterMs);
      }

      await handleStreamingResponse(response, onStreamEvent);
      return; // Success - exit retry loop
    } catch (error) {
      console.error(`Streaming error (attempt ${attempt + 1}/${MAX_RETRIES + 1}):`, error);

      // Determine if error is retryable
      const isRetryableError =
        (error instanceof TypeError && error.message.includes('fetch')) ||
        (error instanceof HttpError && RETRYABLE_STATUS_CODES.includes(error.statusCode)) ||
        (error instanceof Error && isRetryableStreamError(error));

      // If aborted, notify caller and exit without showing error
      if (error instanceof Error && error.name === 'AbortError') {
        onStreamEvent({ type: 'aborted' });
        return;
      }

      // Retry on transient errors (network issues, server restarts during deploy)
      if (isRetryableError && attempt < MAX_RETRIES) {
        // Check if already aborted before waiting
        if (signal?.aborted) {
          onStreamEvent({ type: 'aborted' });
          return;
        }
        // Use Retry-After header if present (for 429 rate limiting), otherwise exponential backoff
        const retryAfter = error instanceof HttpError ? error.retryAfter : undefined;
        let delay: number;
        if (retryAfter) {
          delay = retryAfter;
        } else {
          // Exponential backoff with jitter to avoid thundering herd
          const baseDelay = BASE_DELAY_MS * Math.pow(2, attempt);
          const jitter = (Math.random() - 0.5) * baseDelay * 0.2; // ±10% of base delay
          delay = Math.max(baseDelay + jitter, 100); // Floor to prevent unexpectedly short delays
        }
        console.log(`Retrying in ${delay}ms...`);
        // Signal UI to clear any partial content before retry (prevents duplicate text)
        onStreamEvent({ type: 'retry_reset' });
        onStreamEvent({ type: 'retry', attempt: attempt + 1, maxRetries: MAX_RETRIES });
        // Abort-aware delay to respond immediately if user cancels during wait
        try {
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => {
              signal?.removeEventListener('abort', onAbort);
              resolve();
            }, delay);
            const onAbort = () => {
              clearTimeout(timeout);
              reject(new DOMException('Aborted', 'AbortError'));
            };
            signal?.addEventListener('abort', onAbort, { once: true });
          });
        } catch (e) {
          if (e instanceof DOMException && e.name === 'AbortError') {
            onStreamEvent({ type: 'aborted' });
            return;
          }
          throw e;
        }
        continue;
      }

      // Final attempt failed or non-retryable error - show user-friendly message
      let userMessage = 'An unexpected error occurred. Please try again.';

      if (error instanceof TypeError && error.message.includes('fetch')) {
        userMessage = 'Network connection failed. Please check your internet connection and try again.';
      } else if (error instanceof Error && error.message.includes('HTTP error')) {
        userMessage = 'Server error. Please try again in a moment.';
      } else if (error instanceof Error && error.message.includes('not readable')) {
        userMessage = 'Unable to process the response. Please try again.';
      } else if (error instanceof Error) {
        userMessage = 'Something went wrong while processing your request. Please try again.';
      }

      onStreamEvent({ type: 'error', message: userMessage });
      return;
    }
  }
};

// Helper to check if an error is retryable (network issues, stream interruptions)
// Note: 'aborted' in message catches stream interruptions (e.g., "connection aborted"),
// but user-initiated AbortError is excluded via error.name !== 'AbortError' check
const isRetryableStreamError = (error: Error): boolean => {
  const messageLower = error.message.toLowerCase();
  return (
    error.name !== 'AbortError' &&
    (messageLower.includes('network') ||
      messageLower.includes('interrupted') ||
      messageLower.includes('aborted') ||
      messageLower.includes('failed to fetch'))
  );
};

const handleStreamingResponse = async (response: Response, onStreamEvent: (event: any) => void) => {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Response body is not readable');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            onStreamEvent(data);
          } catch (e) {
            console.warn('Malformed streaming data:', line);
          }
        }
      }
    }
  } catch (error) {
    console.error('Stream reading error:', error);

    // Re-throw retryable errors so caller can handle retry logic
    if (error instanceof Error) {
      if (isRetryableStreamError(error)) {
        throw error; // Let caller retry
      }

      // Non-retryable errors: emit appropriate event
      if (error.name === 'AbortError') {
        // Emit aborted event (not error) for consistent UX with fetch-phase aborts
        onStreamEvent({ type: 'aborted' });
      } else {
        onStreamEvent({ type: 'error', message: 'Unable to receive data from server. Please try again.' });
      }
    } else {
      onStreamEvent({ type: 'error', message: 'Connection was interrupted. Please try again.' });
    }
  }
};

export interface SuggestedQuestionsResponse {
  questions: string[];
}

export const getSuggestedQuestions = async (history: ApiMessage[]): Promise<SuggestedQuestionsResponse> => {
  return (
    await request.post('/suggested_questions/', {
      messages: history,
    })
  ).data;
};

export interface GetEtfBottomLineResponse {
  symbol: string;
  description?: string;
  details: {
    title: string;
    description: string;
    rating: Rating;
  }[];
}

export const getEtfBottomLine = async (symbol: string): Promise<GetEtfBottomLineResponse> =>
  (await request.get(`/investments/${symbol}/etf_bottom_line/`)).data.results;

export interface AllInsightsResponse {
  results: Insight[];
  count: number;
  next: string | null;
  previous: string | null;
}

export const getAllInsights = async (page = 1, symbols?: string): Promise<AllInsightsResponse> => {
  const params: Record<string, string | number | undefined> = {
    page,
    language: getApiLanguage(),
  };
  if (symbols) {
    params.symbols = symbols;
  }

  return (
    await request.get(`/investments/all_insights`, {
      params,
    })
  ).data;
};

export interface TranscriptLine {
  speaker: string;
  title: string;
  content: string;
  sentiment: string;
}

export interface TranscriptResponse {
  symbol: string;
  quarter: string;
  transcript: TranscriptLine[];
}

export interface EarningsTranscriptSummary {
  earningsDate: string;
  quarter: string;
  fiscalDate?: string;
  summary: string | null;
  sentiment: 'positive' | 'neutral' | 'negative' | null;
}

export const getEarningsTranscripts = async (symbol: string): Promise<EarningsTranscriptSummary[]> => {
  return (await request.get(`/investments/${symbol}/earnings`, { params: { language: getApiLanguage() } })).data
    .results;
};

export const getTranscript = async (symbol: string, year: number, quarter: number): Promise<TranscriptResponse> => {
  return (
    await request.get(`/investments/${symbol}/transcript/`, {
      params: { year, quarter },
    })
  ).data.results;
};

interface UserSubscription {
  user: string;
  subscription_type: string;
  expiration_days: number;
}

export const userSubscription = async (userSubscription: UserSubscription) => {
  return (
    await request.post('api/subscription/register/', {
      user: userSubscription.user,
      subscription_type: userSubscription.subscription_type,
      expiration_days: userSubscription.expiration_days,
    })
  ).data;
};

/**
 * Get statistics for a portfolio across different time intervals
 * @param portfolioId - The ID of the portfolio
 * @param intervalSet - The interval set for the statistics
 * @returns A map of time intervals to statistics data
 */
export const getPortfolioStatistics = async (
  portfolioId: string,
  intervalSet: 'intraday' | 'daily',
): Promise<IntervalStatsMap> => {
  const portfolios = await getPortfolios();
  const portfolio = portfolios.find((p) => p.id === portfolioId);

  if (!portfolio) {
    throw new Error(`Portfolio ${portfolioId} not found`);
  }

  // If symbols array is empty but allocations exist, derive symbols and persist
  if ((portfolio.symbols?.length ?? 0) === 0 && portfolio.allocations && portfolio.allocations.length > 0) {
    const derivedSymbols = portfolio.allocations.map((a) => a.symbol);
    await updatePortfolio({ ...portfolio, symbols: derivedSymbols });
    portfolio.symbols = derivedSymbols;
  }

  let symbols: string[] = [];
  let allocations: number[] = [];

  if (portfolio.allocations && portfolio.allocations.length > 0) {
    // Use explicit allocations when present
    symbols = portfolio.allocations.map((allocation: any) => allocation.symbol);

    // Check if all allocation percentages are valid numbers (including numeric strings)
    const hasValidAllocations = portfolio.allocations.every((allocation: any) => {
      const value = allocation.allocation_percentage;
      if (value === null || value === undefined) return false;

      const numericValue = Number(value);
      return !isNaN(numericValue) && isFinite(numericValue);
    });

    if (hasValidAllocations) {
      // Use explicit allocations when all are present and valid (handles both numbers and numeric strings)
      allocations = portfolio.allocations.map((allocation: any) => Number(allocation.allocation_percentage) / 100);
    } else {
      // Fall back to equal-weight allocation when percentages are missing or invalid
      const equalWeight = 1 / portfolio.allocations.length;
      allocations = portfolio.allocations.map(() => equalWeight);
    }
  } else if ((portfolio as any).symbols && (portfolio as any).symbols.length > 0) {
    // Fallback to equal-weight allocation for watchlist symbols
    symbols = (portfolio as any).symbols;
    const equalWeight = 1 / symbols.length;
    allocations = symbols.map(() => equalWeight);
  } else {
    // No allocations or symbols; return empty statistics
    return {} as IntervalStatsMap;
  }

  // Build query params
  const params = new URLSearchParams();
  params.append('symbols', symbols.join(','));
  params.append('allocations', allocations.join(','));
  params.append('interval_set', intervalSet);

  try {
    // Call the backtesting endpoint
    const response = (await request.get(`/backtest/?${params.toString()}`)).data;

    // Handle the nested response structure
    if (response && response.status === 'complete') {
      if (response.interval_results) {
        // Successfully found interval_results (may be empty for stocks without data)
        return response.interval_results as IntervalStatsMap;
      } else {
        // Status is complete, but interval_results is missing
        console.warn(
          'Backtest API response status is complete, but interval_results key is missing or invalid:',
          response,
        );
      }
    } else {
      // Status is not complete or response is invalid
      console.warn('Backtest API response status is not complete or response structure is invalid:', response);
    }

    return {};
  } catch (error) {
    // Gracefully handle 400 errors (e.g., stocks without backtest data)
    const axiosError = error as AxiosError<{ error?: string }>;
    if (axiosError.isAxiosError && axiosError.response?.status === 400) {
      console.warn('Backtest data unavailable for portfolio:', axiosError.response?.data?.error);
      return {};
    }
    // Re-throw other errors to be handled by the caller
    throw error;
  }
};

// Add types for Portfolio Return API
interface PortfolioReturnRequestItem {
  id: string;
  symbols: string[];
  allocations: number[];
  strategy?: string;
  benchmark?: string;
}

interface PortfolioReturnContributor {
  symbol: string;
  return: number;
}

interface PortfolioReturnIntervalData {
  change_percent: number;
  top_contributor: PortfolioReturnContributor | null;
  top_detractor: PortfolioReturnContributor | null;
}

export interface PortfolioReturnResponseItem {
  id: string;
  intervals: Record<'1d' | '1w' | '1m' | '3m' | 'ytd' | '1y' | '5y', PortfolioReturnIntervalData>;
}

interface PortfolioReturnApiResponse {
  portfolios: Array<{
    id: string;
    intervals: Record<string, { return_percent: number; top_contributor?: string; top_detractor?: string }>;
  }>;
}

/**
 * Get return summary statistics for multiple portfolios.
 * @param portfolios - Array of portfolio objects with id, symbols, allocations, etc.
 * @returns An array of portfolio return summaries.
 */
export const getPortfolioReturn = async (
  portfolios: PortfolioReturnRequestItem[],
): Promise<PortfolioReturnResponseItem[]> => {
  const apiPayload = portfolios.map((p) => ({
    ...p,
    strategy: 'buy_and_hold',
    allocations: p.allocations.map((allocPercent) => allocPercent / 100),
  }));
  const response = await request.post<PortfolioReturnApiResponse>('/portfolios/', {
    portfolios: apiPayload,
  });
  const apiPortfolios = response.data.portfolios || [];
  const timeKeys: Array<'1d' | '1w' | '1m' | '3m' | 'ytd' | '1y' | '5y'> = ['1d', '1w', '1m', '3m', 'ytd', '1y', '5y'];
  return apiPortfolios.map((p) => {
    // Handle portfolios with errors or missing intervals
    if ((p as any).error || !p.intervals) {
      console.error(`Error for portfolio ${p.id}: ${(p as any).error || 'No intervals returned'}`);
      const emptyIntervals = timeKeys.reduce(
        (acc, key) => {
          acc[key] = { change_percent: 0, top_contributor: null, top_detractor: null };
          return acc;
        },
        {} as Record<'1d' | '1w' | '1m' | '3m' | 'ytd' | '1y' | '5y', PortfolioReturnIntervalData>,
      );
      return { id: p.id, intervals: emptyIntervals };
    }
    const intervals = timeKeys.reduce(
      (acc, key) => {
        const apiInterval = (p.intervals as any)[key];
        acc[key] = {
          change_percent: apiInterval?.return_percent ?? 0,
          top_contributor: apiInterval?.top_contributor ? { symbol: apiInterval.top_contributor, return: 0 } : null,
          top_detractor: apiInterval?.top_detractor ? { symbol: apiInterval.top_detractor, return: 0 } : null,
        };
        return acc;
      },
      {} as Record<'1d' | '1w' | '1m' | '3m' | 'ytd' | '1y' | '5y', PortfolioReturnIntervalData>,
    );
    return { id: p.id, intervals };
  });
};

export interface NotificationItem {
  id: string;
  title: string;
  body: string;
  sentAt: string;
  read: boolean;
  active: boolean;
  type: string; // Notification type (e.g., 'smart_summary', 'daily_alerts')
  route?: string; // Route information from backend (optional)
  symbols?: string[]; // Array of symbols (optional)
  deviceToken?: string; // Device token (optional, only for smart notifications)
}

export interface PaginatedNotifications {
  deviceToken: string;
  notifications: NotificationItem[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
    nextPage: number | null;
    previousPage: number | null;
  };
  timestamp?: string;
  freshFromDatabase?: boolean;
}

export const getNotifications = async (deviceToken: string, page = 1, pageSize = 20): Promise<PaginatedNotifications> =>
  (
    await request.get('/notifications/by_device_token/', {
      params: { device_token: deviceToken, page, page_size: pageSize },
    })
  ).data;

export const getUnreadNotificationsCount = async (deviceToken: string): Promise<{ unreadCount: number }> => {
  console.log('🌐 Making API call to /notifications/unread_count/ with token:', deviceToken.substring(0, 10) + '...');

  const response = await request.get('/notifications/unread_count/', {
    headers: {
      'Device-Token': deviceToken,
    },
  });

  console.log('🔍 Raw API response:', response);
  console.log('🔍 Response data:', response.data);
  console.log('🔍 Response status:', response.status);

  return response.data;
};

export const markNotificationAsRead = async (notificationId: string, deviceToken: string) =>
  (
    await request.post('/notifications/mark_as_read/', {
      notification_id: notificationId,
      device_token: deviceToken,
    })
  ).data;

export const markMultipleNotificationsRead = async (deviceToken: string) =>
  (
    await request.patch(
      '/notifications/bulk_read/',
      {},
      {
        headers: {
          'Device-Token': deviceToken,
        },
      },
    )
  ).data;

export const getNotificationsBySymbols = async (symbols: string[], limit = 10): Promise<NotificationItem[]> => {
  try {
    // Build query parameters as a plain object – axios will serialize automatically
    const params: Record<string, string> = {
      symbols: symbols.join(','),
    };
    if (limit) {
      params.limit = limit.toString();
    }

    /*
     * NOTE:
     * The backend endpoint uses a dash (by-symbols) instead of an underscore (by_symbols),
     * which differs from most of the other notification endpoints. Keep this as-is to
     * avoid 404 errors.
     */
    const response = await request.get('/notifications/by-symbols/', { params });

    // The API returns an object with a "notifications" array.
    if (response?.data?.notifications && Array.isArray(response.data.notifications)) {
      return response.data.notifications as NotificationItem[];
    }

    // Fallback – if the structure is unexpected, log it and return empty array
    console.warn('Unexpected response format from /notifications/by-symbols/:', response.data);
    return [];
  } catch (error) {
    console.error('Error fetching notifications by symbols:', error);
    return [];
  }
};

// Smart Notifications API

export interface MarketIndicator {
  value: string;
  sentiment: 'positive' | 'negative' | 'neutral';
  url?: string;
}

export interface MarketSentimentData {
  aaii?: MarketIndicator;
  fear_index?: MarketIndicator;
  volatility?: MarketIndicator;
  momentum?: MarketIndicator;
  yield_curve?: MarketIndicator;
  timestamp?: string;
}

export interface BulletPoint {
  text: string;
  sentiment: 'positive' | 'negative' | 'neutral';
}

export interface SmartNotificationSummary {
  id: string;
  title: string;
  content: string;
  generatedAt: string;
  deliveredAt?: string;
  read: boolean;
  bulletPoints?: BulletPoint[];
}

export interface SmartNotificationSettings {
  enabled: boolean;
  frequency: 'daily' | 'weekly';
  notification_time: string; // HH:MM format
}

export const getSmartSummary = async (deviceToken: string): Promise<SmartNotificationSummary | null> => {
  try {
    const response = await request.get('/notifications/smart_summary/', {
      headers: { 'Device-Token': deviceToken },
      params: { language: getApiLanguage() },
    });

    if (response.status === 204) {
      return null;
    }

    return response.data as SmartNotificationSummary;
  } catch (error) {
    console.error('Error fetching smart summary:', error);
    return null;
  }
};

export const markSmartSummaryAsRead = async (summaryId: string, deviceToken: string): Promise<boolean> => {
  try {
    await request.patch(
      '/smart-summaries/mark-read/',
      { summary_id: summaryId },
      { headers: { 'Device-Token': deviceToken } },
    );
    return true;
  } catch (error) {
    console.error('Error marking smart summary as read:', error);
    return false;
  }
};

export const updateSmartNotificationSettings = async (
  deviceToken: string,
  settings: Partial<SmartNotificationSettings>,
): Promise<boolean> => {
  try {
    await request.post('/notifications/update_smart_settings/', settings, { headers: { 'Device-Token': deviceToken } });
    return true;
  } catch (error) {
    console.error('Error updating smart notification settings:', error);
    return false;
  }
};

export const getSmartSummariesHistory = async (
  deviceToken: string,
  limit = 10,
): Promise<SmartNotificationSummary[]> => {
  try {
    const response = await request.get('/smart-summaries/history/', {
      headers: { 'Device-Token': deviceToken },
      params: { limit, language: getApiLanguage() },
    });

    console.log('Response status:', response.status);
    console.log('Response data:', response.data);

    return response.data.summaries as SmartNotificationSummary[];
  } catch (error) {
    console.error('Error fetching smart summaries history:', error);
    return [];
  }
};

// Get today's smart summary
export const getTodaysSmartSummary = async (deviceToken: string): Promise<SmartNotificationSummary | null> => {
  try {
    console.log(
      '[getTodaysSmartSummary] Called with deviceToken:',
      deviceToken ? 'Present (' + deviceToken.slice(0, 20) + '...)' : 'MISSING',
    );

    const response = await request.get('/smart-summaries/today/', {
      headers: { 'Device-Token': deviceToken },
      params: { language: getApiLanguage() },
    });

    console.log('[getTodaysSmartSummary] Response status:', response.status);
    console.log('[getTodaysSmartSummary] Response data:', response.data);

    if (response.status === 204) {
      return null;
    }

    return response.data as SmartNotificationSummary;
  } catch (error: any) {
    console.error('[getTodaysSmartSummary] Error occurred:', error);
    if (error.response) {
      console.error('[getTodaysSmartSummary] Response status:', error.response.status);
      console.error('[getTodaysSmartSummary] Response data:', error.response.data);
      console.error('[getTodaysSmartSummary] Response headers:', error.response.headers);
    } else if (error.request) {
      console.error('[getTodaysSmartSummary] Request was made but no response:', error.request);
    } else {
      console.error('[getTodaysSmartSummary] Error message:', error.message);
    }

    return null;
  }
};

// Get smart summary by ID
export const getSmartSummaryById = async (
  id: string,
  deviceToken: string,
): Promise<SmartNotificationSummary | null> => {
  try {
    const response = await request.get(`/smart-summaries/detail/${id}/`, {
      headers: { 'Device-Token': deviceToken },
      params: { language: getApiLanguage() },
    });

    return response.data as SmartNotificationSummary;
  } catch (error) {
    console.error('Error fetching smart summary by ID:', error);
    return null;
  }
};

// Generate smart summary on-demand
export const generateSmartSummary = async (
  deviceToken: string,
  watchlistSymbols: string[],
): Promise<{ status: 'processing' | 'error'; message: string }> => {
  try {
    const response = await request.post(
      '/smart-summaries/generate/',
      { frequency: 'daily', watchlist_symbols: watchlistSymbols },
      { headers: { 'Device-Token': deviceToken } },
    );

    return { status: 'processing', message: response.data.message || 'Generation started' };
  } catch (error: any) {
    console.error('[generateSmartSummary] Error:', error);
    if (error.response?.status === 429) {
      return { status: 'error', message: 'Rate limit exceeded. Try again later.' };
    }
    return { status: 'error', message: 'Failed to generate summary' };
  }
};

// Get market sentiment data
export const getMarketSentiment = async (): Promise<MarketSentimentData | null> => {
  try {
    console.log('[getMarketSentiment] Fetching market sentiment data');

    const response = await request.get('/market-sentiment/');

    console.log('[getMarketSentiment] Response status:', response.status);
    console.log('[getMarketSentiment] Response data:', response.data);

    if (response.data.status === 'success') {
      return response.data.data as MarketSentimentData;
    }

    return null;
  } catch (error: any) {
    console.error('[getMarketSentiment] Error occurred:', error);
    if (error.response) {
      console.error('[getMarketSentiment] Response status:', error.response.status);
      console.error('[getMarketSentiment] Response data:', error.response.data);
    }
    return null;
  }
};

// Market Map types and API
export interface MarketMapStock {
  symbol: string;
  name: string;
  market_cap: number;
  change_percent: number | null;
}

export interface MarketMapSector {
  name: string;
  market_cap: number;
  change_percent: number;
  stocks: MarketMapStock[];
}

export interface MarketMapData {
  sectors: MarketMapSector[];
  period: string;
}

export type MarketMapPeriod = '1d' | '1w' | '1m' | '3m' | '6m' | 'ytd' | '1y' | '5y';

export const getMarketMap = async (period: MarketMapPeriod = '1d'): Promise<MarketMapData | null> => {
  try {
    const response = await request.get(`/market-map/?period=${period}`);

    if (response.data.status === 'success') {
      return response.data.data as MarketMapData;
    }

    return null;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { api: 'getMarketMap' },
      extra: { period },
    });
    return null;
  }
};

// Top Movers types and API
export interface TopMoverStock {
  symbol: string;
  name: string;
  sector: string;
  change_percent: number;
  market_cap: number;
  price?: number;
}

export type TopMoversUniverse = 'all' | 'sp500' | 'international' | 'crypto';

export interface TopMoversData {
  stocks: TopMoverStock[];
  period: string;
  sector: string;
  universe: string;
  sectors: string[];
}

export const getTopMovers = async (
  period: MarketMapPeriod = '1d',
  sector = 'all',
  limit = 24,
  universe: TopMoversUniverse = 'all',
): Promise<TopMoversData | null> => {
  try {
    // URL-encode sector to handle names with spaces or special characters (e.g., "Energy & Transportation")
    const response = await request.get(
      `/market-map/top-movers/?period=${period}&sector=${encodeURIComponent(sector)}&limit=${limit}&universe=${universe}`,
    );

    if (response.data.status === 'success') {
      return response.data.data as TopMoversData;
    }

    return null;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { api: 'getTopMovers' },
      extra: { period, sector, limit, universe },
    });
    return null;
  }
};

// Market Overview types and API (for ETF/index treemaps)
export type MarketOverviewView = 'sectors' | 'global' | 'indexes' | 'industries';

export interface MarketOverviewStock {
  symbol: string;
  display_name: string;
  name: string;
  sector: string;
  change_percent: number;
  market_cap: number;
}

export interface MarketOverviewData {
  stocks: MarketOverviewStock[];
  period: string;
  view: string;
}

export const getMarketOverview = async (
  period: MarketMapPeriod = '1d',
  view: MarketOverviewView = 'sectors',
): Promise<MarketOverviewData | null> => {
  try {
    const response = await request.get(`/market-map/overview/?period=${period}&view=${view}`);

    if (response.data.status === 'success') {
      return response.data.data as MarketOverviewData;
    }

    return null;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { api: 'getMarketOverview' },
      extra: { period, view },
    });
    return null;
  }
};

export interface PortfolioKeyEvent {
  id: string;
  title: string;
  body: string;
  type: string;
  symbols: string[];
  created_at: string;
  route: string;
  data: Record<string, any>;
}

export interface PortfolioKeyEventsResponse {
  symbols: string[];
  events: PortfolioKeyEvent[];
  count: number;
}

const MAX_SYMBOLS_FOR_KEY_EVENTS = 100;

export const getPortfolioKeyEvents = async (symbols: string[]): Promise<PortfolioKeyEventsResponse> => {
  try {
    // Limit symbols to avoid 400 error from backend (max 100 symbols)
    const limitedSymbols = symbols.slice(0, MAX_SYMBOLS_FOR_KEY_EVENTS);
    const response = await request.post('/portfolios/key-events/', {
      symbols: limitedSymbols,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching portfolio key events:', error);
    return { symbols: [], events: [], count: 0 };
  }
};

// Portfolio News Article (Real news from Benzinga/IEX)
export interface PortfolioNewsArticle {
  id: string;
  headline: string;
  summary: string;
  imageUrl?: string;
  articleUrl: string;
  publishedAt: string;
  symbols: string[];
  source: string;
}

export interface PortfolioNewsResponse {
  articles: PortfolioNewsArticle[];
  count: number;
  symbols: string[];
}

export const getPortfolioNews = async (symbols: string[]): Promise<PortfolioNewsResponse> => {
  try {
    const symbolsParam = symbols.join(',');
    const response = await request.get('/smart-summaries/portfolio-news/', {
      params: { symbols: symbolsParam, language: getApiLanguage() },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching portfolio news:', error);
    return { articles: [], count: 0, symbols: [] };
  }
};

export const getGeneralMarketNews = async (): Promise<PortfolioNewsResponse> => {
  const response = await request.get('/smart-summaries/portfolio-news/', {
    params: { language: getApiLanguage() },
  });
  return response.data;
};

export interface SymbolKeyEventsResponse {
  symbol: string;
  events: PortfolioKeyEvent[];
  count: number;
  total_count?: number;
  has_more?: boolean;
  next_offset?: number | null;
}

export const getSymbolKeyEvents = async (
  symbol: string,
  limit?: number,
  offset?: number,
  includeStale = false,
): Promise<SymbolKeyEventsResponse> => {
  try {
    // Validate and clamp parameters
    const validLimit = limit ? Math.min(Math.max(limit, 1), 100) : undefined;
    const validOffset = offset ? Math.max(offset, 0) : undefined;

    const params: Record<string, string> = {};
    if (validLimit) params.limit = validLimit.toString();
    if (validOffset) params.offset = validOffset.toString();
    if (includeStale) params.include_stale = 'true';
    const lang = getApiLanguage();
    if (lang) params.language = lang;

    const response = await request.get(`/investments/${symbol}/key_events/`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching symbol key events:', error);
    return { symbol, events: [], count: 0, total_count: 0, has_more: false };
  }
};
